import { Config } from "@oclif/core/config"
import { CommandError, Input, ParserOutput } from "@oclif/core/interfaces"
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { ArgOutput, FlagOutput } from "@oclif/core/lib/interfaces/parser.js"
import * as Sentry from "@sentry/node"
import { isAxiosError } from "axios"

import { BaseCommand } from "./base-command.js"
import { NotAuthorizedError } from "./not-authorized.error.js"
import { CLIError } from "@oclif/core/errors"
import { RequestExecutorError } from "@supernova-studio/client"
import { createApiClient } from "../utils/http-client.js"
import { getTargetEnv } from "./environment.js"

export abstract class SentryCommand<T extends object> extends BaseCommand<T> {
  protected constructor(argv: string[], config: Config) {
    super(argv, config)
  }

  async catch(error: CommandError & { oclif?: { exit: number } }) {
    if (!(error instanceof CLIError)) {
      let { message } = error
      if (error instanceof RequestExecutorError) {
        message = error.errorCode ? `${error.errorCode}: ${error.getDisplayMessage()}` : error.getDisplayMessage()
      }

      // Invocations of `this.error(...)` from command classes will already be logged, no need to repeat
      this.logToStderr(message)
    }

    if (error.oclif) throw error
    if (error instanceof NotAuthorizedError) throw error

    if (isAxiosError(error)) {
      const captureMetadata = {
        code: error.code,
        message: error.message,
        response: {
          status: error.response?.status,
          data: error.response?.data,
        },
      }

      Sentry.captureMessage(`Axios error metadata: ${JSON.stringify(captureMetadata)}`)
    }

    Sentry.captureException(error)
    this.logToStderr(`Something went wrong. Please try again later.`)
  }

  async finally() {}

  private static readonly SKIP_VERSION_CHECK = new Set(["help", "login", "logout", "whoami"])

  private async checkCliVersion(flags: Record<string, unknown>) {
    if (!this.id || SentryCommand.SKIP_VERSION_CHECK.has(this.id)) return

    let result: { supported: boolean; minVersion?: string; message?: string } | undefined
    try {
      const client = await createApiClient(getTargetEnv())
      const response = await client.get<{ result: typeof result }>(`/cli/compatibility`, {
        params: {
          command: this.id,
          version: this.config.version,
          workspaceId: flags.workspaceId,
          designSystemId: flags.designSystemId,
        },
      })
      result = response.data.result
    } catch {
      // Don't block CLI if version check fails on BE side
      return
    }

    if (result && !result.supported) {
      const updateInstruction = result.minVersion
        ? `Please update the CLI to version ${result.minVersion} or later to continue:\nnpm install -g @supernova-studio/cli@latest`
        : `Please update the CLI to continue:\nnpm install -g @supernova-studio/cli@latest`
      this.error(result.message ? `${result.message}\n${updateInstruction}` : updateInstruction)
    }
  }

  public async parse<F extends FlagOutput, B extends FlagOutput, A extends ArgOutput>(
    options?: Input<F, B, A>,
    argv?: string[],
  ): Promise<ParserOutput<F, B, A>> {
    Sentry.addBreadcrumb({
      category: "command",
      data: {
        commandName: this.id,
      },
      level: "debug",
      message: "Starting command parse",
    })

    const parsed = await super.parse(options, argv)
    const span = Sentry.getActiveSpan()
    if (span) {
      span.setAttributes({
        args: Object.keys(parsed.args),
        designSystemId: parsed.flags.designSystemId,
        flags: Object.keys(parsed.flags),
        workspaceId: parsed.flags.workspaceId,
      })
    }

    Sentry.addBreadcrumb({
      category: "command",
      data: {
        result: "success",
      },
      level: "debug",
      message: "Command parse completed",
    })

    await this.checkCliVersion(parsed.flags)

    return parsed
  }
}
